<!--
  SettingsPanel · 设置面板
  功能：
    · 左侧：学习模式预设列表（内置3 + 自定义N），点击即应用
    · 右侧：当前选中预设的可视化编辑器（名称/描述/阶梯步骤 增删改）
    · 新建模式、克隆（内置→自定义）、删除自定义、保存
  说明：所有预设持久化在 localStorage（自定义才存，内置始终固定）
-->
<template>
  <details class="adv" open>
    <summary>⚙️ 设置 · 学习模式 / 快捷键帮助</summary>

    <div class="settings-wrap">
      <!-- ============ 左：预设列表 ============ -->
      <div class="presets-col">
        <div class="col-title">
          <b>学习模式</b>
          <button class="mini-btn" @click="onNewPreset" title="新建自定义模式">+ 新建</button>
        </div>
        <div class="preset-list">
          <div
            v-for="p in state.presets"
            :key="p.id"
            class="preset-item"
            :class="{active: state.activePresetId === p.id, edit: editingId === p.id}"
            @click="onSelectPreset(p.id)"
          >
            <div class="row preset-head">
              <span class="pname">{{ p.name }}</span>
              <span class="tag builtin" v-if="p.builtIn">内置</span>
              <span class="tag" v-else>自定义</span>
            </div>
            <div class="pdesc">{{ p.desc || '（无描述）' }}</div>
            <div class="pstairs">{{ stairPreview(p) }}</div>
            <div class="row preset-actions" v-if="editingId === p.id" @click.stop>
              <button class="mini-btn" @click.stop="onStartEdit(p)"
                      v-if="!editDraft" title="打开右侧编辑器">编辑</button>
              <button class="mini-btn" @click.stop="onClone(p.id)"
                      title="克隆为自定义模式（内置预设只能克隆后编辑）">克隆</button>
              <button class="mini-btn danger" @click.stop="onDelete(p.id)"
                      v-if="!p.builtIn" title="删除该自定义预设">删除</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ 右：编辑区 ============ -->
      <div class="editor-col" v-if="editDraft">
        <div class="col-title">
          <b>{{ editDraft.builtIn ? '克隆并编辑' : (editDraft.id ? '编辑模式' : '新建模式') }}</b>
          <button class="mini-btn" @click="editDraft = null" title="放弃编辑">关闭 ✕</button>
        </div>

        <label class="f">
          <span>名称</span>
          <input v-model.trim="editDraft.name" maxlength="24" placeholder="给模式起个名">
        </label>
        <label class="f">
          <span>描述</span>
          <input v-model.trim="editDraft.desc" maxlength="80" placeholder="一句话说明这个模式的用途">
        </label>

        <div class="opts-row">
          <label class="chk">
            <input type="checkbox" v-model="editDraft.adaptive">
            启用阶梯（否则按第一档倍速顺序播放，每句 1 次）
          </label>
        </div>

        <label class="f gap-field">
          <span>句间停顿（秒）</span>
          <div class="gap-input-row">
            <input type="number" min="0" max="30" step="0.1" v-model.number="editDraft.pause_gap">
            <span class="gap-hint">0 = 不停顿 · 0.5s 听出重复 · 1.5s 便于跟读</span>
          </div>
        </label>

        <!-- 阶梯可视化编辑 -->
        <div class="stairs-editor">
          <div class="stair-hint" v-if="editDraft.adaptive">
            句子会按顺序播放下方每一档：先 {{ rateTxt(editDraft.staircase[0]?.rate) }} 播放
            {{ editDraft.staircase[0]?.repeat || 0 }} 次，再 {{ rateTxt(editDraft.staircase[1]?.rate) }}…
            所有档位完成后自动进入下一句。
          </div>
          <div class="stair-hint" v-else>
            关闭阶梯后，直接按下方第一档 <b>{{ rateTxt(editDraft.staircase[0]?.rate) }}</b> 播放每句 1 次。
          </div>

          <div
            v-for="(s, i) in editDraft.staircase"
            :key="i"
            class="stair-row"
          >
            <span class="stair-idx">第 {{ i + 1 }} 档</span>

            <span class="lbl">倍速：</span>
            <select v-model.number="s.rate">
              <option v-for="r in [0.4,0.5,0.6,0.7,0.75,0.8,0.9,1.0,1.2,1.5,1.8,2.0]" :key="r" :value="r">
                {{ r.toFixed(2).replace(/0$/,'').replace(/\.$/,'.0') }}×
              </option>
            </select>

            <span class="lbl">重复：</span>
            <input type="number" min="1" max="99" v-model.number="s.repeat">
            <span class="lbl">次</span>

            <button class="mini-btn"
                    :disabled="editDraft.staircase.length <= 1"
                    @click="removeStair(i)" title="删除这一档">🗑</button>
          </div>

          <button class="mini-btn add-stair"
                  v-if="editDraft.adaptive && editDraft.staircase.length < 6"
                  @click="addStair">+ 添加一档</button>
        </div>

        <div class="row save-row">
          <button class="primary" @click="onSaveDraft" :disabled="!canSave">
            💾 保存并应用
          </button>
          <button @click="editDraft = null">取消</button>
          <span v-if="err" style="color:var(--bad);font-size:13px">{{ err }}</span>
        </div>
      </div>

      <!-- 关闭编辑时的占位 -->
      <div class="editor-col empty-col" v-else>
        <div class="col-title"><b>说明</b></div>
        <p>👉 点击左侧任一模式<b>立即生效</b>并开始播放；
        点「克隆」可把内置模式改成你自己的版本，点「新建」从零开始。</p>
        <p>📦 自定义的模式会保存在浏览器本地（localStorage），清除浏览器数据会丢失。</p>
      </div>
    </div>

    <!-- 快捷键帮助 -->
    <div class="help">
      <b>快捷键：</b>
      <kbd>Space</kbd> 播放/暂停 ·
      <kbd>←</kbd><kbd>→</kbd> 上/下一句 ·
      <kbd>R</kbd> 重放当前 ·
      <kbd>L</kbd> 锁定单句 ·
      <kbd>A</kbd> 切换阶梯 ·
      <kbd>[</kbd><kbd>]</kbd> 调速 ±0.1 ·
      <kbd>1/2/3/4</kbd> 切换字幕（关/EN/中/双语）
    </div>
  </details>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
  state,
  applyPreset,
  upsertPreset,
  deletePreset,
  clonePreset,
  toast,
} from '@/composables/usePlayerState'

const SPEED_CHOICES = [0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0, 1.2, 1.5, 1.8, 2.0]

const emit = defineEmits(['apply'])  // 父组件用来在 apply 后同步倍速按钮高亮等

// —— 编辑中的草稿对象；null = 未编辑 —— //
const editDraft = ref(null)
const editingId = ref(null)   // 正在编辑/选中哪个 preset（仅用于左侧高亮编辑按钮）
const err = ref('')

const canSave = computed(() => {
  const d = editDraft.value
  if (!d) return false
  if (!String(d.name || '').trim()) return false
  if (!Array.isArray(d.staircase) || !d.staircase.length) return false
  return d.staircase.every(s =>
    typeof s.rate === 'number' && s.rate > 0 &&
    Number.isInteger(s.repeat) && s.repeat >= 1
  )
})

// —— 辅助函数 —— //
function rateTxt(r) {
  if (r == null) return '-'
  // 保留到 2 位小数再去掉多余的 0（避免 0.75 这类值被 toFixed(1) 四舍五入成 0.8）
  return (+r).toFixed(2).replace(/0$/, '').replace(/\.$/, '.0') + '×'
}
function stairPreview(p) {
  if (!p) return ''
  const steps = p.staircase || []
  const base = !p.adaptive
    ? `${rateTxt(steps[0]?.rate)} × 1次 / 句`
    : steps.map(s => `${rateTxt(s.rate)}×${s.repeat}`).join(' → ')
  const gap = p.pause_gap > 0 ? `  ·  ⏸${p.pause_gap}s` : ''
  const lock = p.lock_one ? '  ·  🔒 锁定单句' : ''
  return base + gap + lock
}

// —— 预设列表交互 —— //
function onSelectPreset(id) {
  applyPreset(id)
  emit('apply')
  toast('已切换：' + (state.presets.find(p => p.id === id)?.name || ''))
  // 选中后让右侧不自动弹出编辑面板；用户想改时再点「克隆/编辑」
  editingId.value = null
  editDraft.value = null
}

function onStartEdit(p) {
  if (p.builtIn) {
    // 内置不能直接改，先克隆一个再编辑
    const newId = clonePreset(p.id)
    if (!newId) return
    const np = state.presets.find(x => x.id === newId)
    openDraft(np)
  } else {
    openDraft(p)
  }
}

function onClone(id) {
  const newId = clonePreset(id)
  if (!newId) return
  const np = state.presets.find(x => x.id === newId)
  openDraft(np)
  toast('✅ 已克隆为自定义模式')
}

function onDelete(id) {
  const p = state.presets.find(x => x.id === id)
  if (!p) return
  const ok = window.confirm(`确定删除自定义模式「${p.name}」？`)
  if (!ok) return
  deletePreset(id)
  if (editingId.value === id) { editingId.value = null; editDraft.value = null }
  emit('apply')
  toast('已删除')
}

function onNewPreset() {
  const blank = {
    id: null,
    name: '我的新模式',
    desc: '',
    builtIn: false,
    adaptive: true,
    lock_one: false,
    pause_gap: 0.5,
    staircase: [{ rate: 0.8, repeat: 2 }, { rate: 1.0, repeat: 1 }],
  }
  openDraft(blank)
}

function openDraft(p) {
  if (!p) return
  editingId.value = p.id
  // 深拷贝一份，避免取消编辑时脏了原对象
  editDraft.value = {
    id: p.id || null,
    name: p.name || '自定义模式',
    desc: p.desc || '',
    builtIn: !!p.builtIn,
    adaptive: !!p.adaptive,
    lock_one: !!p.lock_one,
    pause_gap: +p.pause_gap || 0,
    staircase: (p.staircase || [{ rate: 1.0, repeat: 1 }])
      .map(s => ({ rate: +s.rate, repeat: +s.repeat })),
  }
  err.value = ''
}

// —— 阶梯编辑 —— //
function addStair() {
  const d = editDraft.value
  if (!d) return
  const last = d.staircase[d.staircase.length - 1]
  const rate = Math.min(2.0, +(last ? Math.min(2.0, +last.rate + 0.1) : 1.0).toFixed(1))
  d.staircase.push({ rate, repeat: 2 })
}
function removeStair(i) {
  const d = editDraft.value
  if (!d || d.staircase.length <= 1) return
  d.staircase.splice(i, 1)
}

// —— 保存草稿（新建或更新自定义） —— //
function onSaveDraft() {
  err.value = ''
  const d = editDraft.value
  if (!d) return
  if (!canSave.value) {
    err.value = '请检查：名称非空、阶梯至少 1 档、每档倍速/重复值都合理'
    return
  }
  // 如果是从内置点出来的「克隆+编辑」，保存时 id=null 会作为新建自定义入库
  const savePayload = { ...d }
  if (savePayload.builtIn) savePayload.id = null   // 内置不能覆盖，只能新建
  const newId = upsertPreset(savePayload)
  applyPreset(newId)
  emit('apply')
  editingId.value = newId
  editDraft.value = null
  toast('✅ 已保存并应用：' + (state.presets.find(x => x.id === newId)?.name || ''))
}
</script>

<style scoped>
.settings-wrap{
  display:grid;
  grid-template-columns: 260px 1fr;
  gap: 14px;
  margin-top: 10px;
}
@media (max-width: 860px) {
  .settings-wrap { grid-template-columns: 1fr; }
}
.col-title{
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom: 8px;
  color: var(--ink);
  font-size: 14px;
}
.presets-col, .editor-col{
  background: var(--bg2);
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 12px;
}
.empty-col p { color: var(--mute); line-height: 1.7; margin: 6px 0; }
.preset-list{ display:flex; flex-direction:column; gap:8px; max-height: 360px; overflow:auto; }
.preset-item{
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: 6px;
  cursor: pointer;
  background: var(--bg1);
  transition: all .15s ease;
}
.preset-item:hover{ border-color: var(--pri); }
.preset-item.active{
  border-color: var(--pri);
  background: linear-gradient(180deg, var(--pri-bg), var(--bg1));
  box-shadow: 0 0 0 2px var(--pri-bg);
}
.preset-head{ gap: 6px; }
.pname{ font-weight:600; color: var(--ink); }
.tag{
  font-size: 11px; padding: 1px 6px; border-radius: 4px;
  background: var(--line); color: var(--mute);
}
.tag.builtin{ background: #1f6feb33; color: #58a6ff; }
.pdesc{ color: var(--mute); font-size: 12px; margin: 4px 0; line-height: 1.45; }
.pstairs{ color: var(--ink); font-size: 12px; font-family: ui-monospace, monospace; }
.preset-actions{ margin-top: 6px; gap: 6px; flex-wrap: wrap; }
.f{ display:flex; flex-direction:column; gap:4px; margin-bottom: 8px; }
.f > span{ font-size: 12px; color: var(--mute); }
.f input, .f select {
  background: var(--bg1); color: var(--ink);
  border: 1px solid var(--line); border-radius: 5px; padding: 6px 8px;
}
.opts-row{ margin: 6px 0 10px; }
.chk{ color: var(--ink); font-size: 13px; display:inline-flex; align-items:center; gap: 6px; }
.gap-field{ margin-bottom: 10px; }
.gap-input-row{ display: flex; align-items: center; gap: 8px; }
.gap-input-row input{ width: 80px; }
.gap-hint{ font-size: 11px; color: var(--mute); }
.stairs-editor{
  border-top: 1px dashed var(--line);
  border-bottom: 1px dashed var(--line);
  padding: 10px 0;
  margin-bottom: 10px;
}
.stair-hint{ color: var(--mute); font-size: 12px; margin-bottom: 8px; line-height: 1.6; }
.stair-row{
  display: grid;
  grid-template-columns: 70px 60px auto 60px auto 50px 40px;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}
@media (max-width: 600px){
  .stair-row { grid-template-columns: 60px 50px auto 50px auto 40px 40px; font-size: 12px; }
}
.stair-row select, .stair-row input[type=number]{
  background: var(--bg1); color: var(--ink);
  border: 1px solid var(--line); border-radius: 5px; padding: 4px 6px;
}
.stair-idx{ color: var(--pri); font-weight: 600; font-size: 12px; }
.lbl{ color: var(--mute); font-size: 12px; text-align: right; }
.add-stair{ margin-top: 6px; }
.save-row{ gap: 10px; }
.mini-btn{
  font-size: 12px;
  padding: 2px 8px;
  border: 1px solid var(--line);
  background: var(--bg1);
  color: var(--ink);
  border-radius: 5px;
  cursor: pointer;
}
.mini-btn:hover:not(:disabled){ border-color: var(--pri); color: var(--pri); }
.mini-btn:disabled{ opacity: .45; cursor: not-allowed; }
.mini-btn.danger{ color: #ff6b6b; border-color: #ff6b6b66; }
.mini-btn.danger:hover{ background: #ff6b6b22; }
.help{
  margin-top: 14px;
  padding: 10px 12px;
  background: var(--bg2);
  border: 1px solid var(--line);
  border-radius: 6px;
  color: var(--mute);
  font-size: 13px;
  line-height: 1.9;
}
kbd{
  background: var(--bg1);
  border: 1px solid var(--line);
  border-bottom-width: 2px;
  border-radius: 4px;
  padding: 0 5px;
  margin: 0 2px;
  font-family: ui-monospace, monospace;
  font-size: 11px;
  color: var(--ink);
}
</style>
